import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Briefcase,
  Settings,
  LogOut,
  Bell,
  ArrowLeft,
  Search,
  Sparkles,
  Building,
  MapPin,
  Clock,
  DollarSign,
  TrendingUp,
} from 'lucide-react';
import { Slider } from '../ui/slider';
import { useJobMatchesQuery, useMatchFeedback } from '../../hooks/useCandidateData';
import type { JobMatch } from '../../api/jobMatches';
import { toast } from 'sonner';
import { isEmployerVerified } from '../../lib/jobVerification';

const PREFERENCES_STORAGE_KEY = 'candidate_job_matches_preferences';

export function JobMatches() {
  const [threshold, setThreshold] = useState(70);
  const [searchTerm, setSearchTerm] = useState('');

  const {
    data: matches = [],
    isPending,
    isError,
    refetch,
  } = useJobMatchesQuery();
  const matchFeedbackMutation = useMatchFeedback();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(PREFERENCES_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { threshold?: number; search?: string };
      if (typeof parsed.threshold === 'number') setThreshold(parsed.threshold);
      if (typeof parsed.search === 'string') setSearchTerm(parsed.search);
    } catch {
      // ignore errors
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        PREFERENCES_STORAGE_KEY,
        JSON.stringify({ threshold, search: searchTerm }),
      );
    } catch {
      // ignore
    }
  }, [threshold, searchTerm]);

  const filteredMatches = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return matches.filter(
      (match) =>
        (match.matchScore ?? 0) >= threshold &&
        (!normalizedSearch ||
          match.title.toLowerCase().includes(normalizedSearch) ||
          match.company.toLowerCase().includes(normalizedSearch)),
    );
  }, [matches, threshold, searchTerm]);

  const stats = useMemo(
    () => [
      { label: 'Total Matches', value: matches.length },
      { label: 'Excellent Match', value: matches.filter((match) => (match.matchScore ?? 0) >= 90).length },
      { label: 'Trending Now', value: matches.filter((match) => match.trending).length },
      { label: 'Verified Employers', value: matches.filter((match) => isEmployerVerified(match)).length },
    ],
    [matches],
  );

  const handleFeedback = (matchId: string | number, feedback: 'positive' | 'negative') => {
    matchFeedbackMutation.mutate(
      { matchId, feedback },
      {
        onSuccess: () => {
          toast.success(
            feedback === 'positive'
              ? 'Great! We will surface similar roles.'
              : 'Thanks, we will refine future matches.',
          );
        },
        onError: (error) => {
          const message = error instanceof Error ? error.message : 'Unable to send feedback.';
          toast.error(message);
        },
      },
    );
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[radial-gradient(circle_at_6%_10%,rgba(99,102,241,0.18),transparent_52%),linear-gradient(180deg,#030712_0%,#020816_70%,#01020a_100%)]">
      <header className="border-b border-white/10 bg-[rgba(5,9,19,0.82)] backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gold-gradient flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-black" />
              </div>
              <span className="text-gold-gradient">Lighthouse Jobs</span>
            </Link>
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="relative text-white">
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#ba9036] rounded-full" />
              </Button>
              <Link to="/life">
                <Button variant="outline" className="border-[#d2ac47]/50 text-[#ba9036] hover:bg-[#ba9036]/10">
                  Life Platform
                </Button>
              </Link>
              <Link to="/candidate/settings">
                <Button variant="ghost" size="icon" className="text-white">
                  <Settings className="w-5 h-5" />
                </Button>
              </Link>
              <Link to="/">
                <Button variant="ghost" size="icon" className="text-white">
                  <LogOut className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link to="/candidate/dashboard">
            <Button variant="ghost" className="text-gray-400 hover:text-white mb-4 -ml-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="mb-2 text-gold-gradient">AI Job Matches</h1>
          <p className="text-gray-400">Personalized job recommendations based on your profile</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {isPending
            ? Array.from({ length: 4 }).map((_, index) => (
                <Card key={`match-stat-skeleton-${index}`} className="bg-white/5 border-white/10">
                  <CardContent className="p-6 space-y-3">
                    <div className="h-3 w-24 animate-pulse rounded bg-white/10" />
                    <div className="h-6 w-16 animate-pulse rounded bg-white/10" />
                  </CardContent>
                </Card>
              ))
            : stats.map((stat) => (
                <Card key={stat.label} className="bg-white/5 border-white/10">
                  <CardContent className="p-6">
                    <div className="text-white mb-1">{stat.value}</div>
                    <p className="text-sm text-gray-400">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
        </div>

        <Card className="bg-white/5 border-white/10 mb-6">
          <CardContent className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search matches..."
                  className="pl-10 bg-white/10 border-white/20 text-white"
                />
              </div>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
                <div className="text-sm text-gray-400">Match score threshold: {threshold}%</div>
                <Slider value={[threshold]} onValueChange={([value]) => setThreshold(value)} min={50} max={100} step={5} className="w-full md:w-64" />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {isPending ? (
            <JobMatchesSkeleton />
          ) : (
            filteredMatches.map((match) => (
              <JobMatchCard
                key={match.id}
                match={match}
                onFeedback={handleFeedback}
                feedbackPending={matchFeedbackMutation.isPending}
              />
            ))
          )}

          {filteredMatches.length === 0 && !isPending && (
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-12 text-center">
                <Sparkles className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-white mb-2">No matches at this threshold</h3>
                <p className="text-gray-400">Lower the match score threshold or refresh your profile to discover more roles.</p>
              </CardContent>
            </Card>
          )}
        </div>

        {isError && (
          <Card className="bg-red-500/10 border border-red-500/40 mt-6">
            <CardContent className="p-4 flex items-center justify-between text-sm text-red-100">
              <span>We couldn&apos;t load your matches. Please try again.</span>
              <Button variant="outline" size="sm" className="border-red-300 text-red-200" onClick={() => refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function JobMatchCard({
  match,
  onFeedback,
  feedbackPending,
}: {
  match: JobMatch;
  onFeedback: (matchId: string | number, feedback: 'positive' | 'negative') => void;
  feedbackPending: boolean;
}) {
  return (
    <Card className="bg-white/5 border-white/10 overflow-hidden">
      <CardContent className="p-6 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gold-gradient flex items-center justify-center">
                <Building className="w-5 h-5 text-black" />
              </div>
              <div>
                <h3 className="text-white">{match.title}</h3>
                <div className="flex items-center gap-4 text-sm text-gray-400 flex-wrap">
                  <span>{match.company}</span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {match.location}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {match.type}
                  </span>
                  {match.salary && (
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      {match.salary}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-300">{match.description}</p>

            <div className="flex flex-wrap gap-2">
              {(match.requirements ?? []).slice(0, 4).map((req) => (
                <Badge key={req} variant="outline" className="border-white/20 text-gray-300">
                  {req}
                </Badge>
              ))}
            </div>
            <div className="flex gap-3 text-xs text-gray-400">
              <Button
                variant="ghost"
                size="sm"
                className="text-green-300 hover:text-green-100 p-0"
                disabled={feedbackPending}
                onClick={() => onFeedback(match.id, 'positive')}
              >
                👍 Keep similar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-300 hover:text-red-100 p-0"
                disabled={feedbackPending}
                onClick={() => onFeedback(match.id, 'negative')}
              >
                👎 Not relevant
              </Button>
            </div>
          </div>

          <div className="w-full lg:w-64 space-y-3">
            <div className="text-center">
              <p className="text-sm text-gray-400">Match Score</p>
              <p className="text-4xl text-white">{match.matchScore ?? '—'}%</p>
              {match.matchScore && match.matchScore >= 90 && (
                <Badge className="mt-2 bg-green-500/20 text-green-400 border-green-500/50">
                  <TrendingUp className="w-3 h-3 mr-1" /> Excellent match
                </Badge>
              )}
            </div>
            <div className="space-y-2">
              {(match.matchReasons ?? []).map((reason) => (
                <div key={reason.reason} className="rounded-lg border border-white/10 bg-white/5 p-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-300">{reason.reason}</span>
                    <span className="text-[#ba9036]">{reason.score}%</span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-white/10">
                    <div className="h-full rounded-full bg-gold-gradient" style={{ width: `${reason.score}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
            Save Job
          </Button>
          <Button className="bg-gold-gradient text-black hover:brightness-110">Apply Now</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function JobMatchesSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, index) => (
        <Card key={`match-skeleton-${index}`} className="bg-white/5 border-white/10 overflow-hidden">
          <CardContent className="p-6 space-y-4">
            <div className="h-6 w-1/3 animate-pulse rounded bg-white/10" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-white/10" />
            <div className="h-14 w-full animate-pulse rounded bg-white/10" />
          </CardContent>
        </Card>
      ))}
    </>
  );
}
